import React from "react"
import { useGame } from "hooks/useGame"
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Box, HeaderBody, SpacedPaper, Collapse, Alert, HeaderTertiary } from "@fedsim/components";
import { AlertColor } from "@mui/material";
import { DURATIONTIMEOUT } from '@fedsim/constants'

import FSXLogo from 'components/fsx-logo'
import { Off, On } from "icons"

const modelContent = {
  alertAddSegments: (
    <Box>
      <HeaderTertiary>Get Bookin Pal!</HeaderTertiary><br />
      <HeaderBody>Add segments, promos and title matches to plan your show.
        Add factions, feuds and wrestlers to a segment to get ratings.</HeaderBody>
    </Box>
  ),
  alertAlignmentIntro: (
    <Box>
      <HeaderTertiary>Tip the scales</HeaderTertiary><br />
      <HeaderBody>Maybe its time to spice things up by turning that ultimate babyface into a next level heel?</HeaderBody>
      <HeaderBody>Filter and update all your wrestlers below, then assign their alignment to confirm the change</HeaderBody>
    </Box>
  ),
  alertCMSCreate: (
    <Box>
      <HeaderTertiary>Create your own!</HeaderTertiary><br />
      <HeaderBody>Add your own items to play with, from scratch!</HeaderBody>
    </Box>
  ),
  alertCMSImport: (
    <Box>
      <HeaderTertiary>Import more items</HeaderTertiary><br />
      <HeaderBody>Import items from each area, remember to then assign them to brands to play with them in the office.</HeaderBody>
    </Box>
  ),
  alertCMSOverview: (
    <Box>
      <HeaderBody>
        View, update, duplicate and manage items in your system below
      </HeaderBody>
    </Box>
  ),
  alertDraftComparison: (
    <Box>
      <HeaderTertiary>Apples to oranges?</HeaderTertiary><br />
    </Box>
  ),
  alertDraftComplete: (
    <Box>
      <HeaderTertiary>Ready to commit?</HeaderTertiary><br />
      <HeaderBody>Choices made, votes cast, let the move commence - confirm when ready</HeaderBody>
    </Box>
  ),
  alertDraftGameBoard: (
    <Box>
      <HeaderTertiary>Board in play</HeaderTertiary><br />
      <HeaderBody>Take turns between brands to pick a wrestler to draft.</HeaderBody>
      <HeaderBody>Or set it to stick and pick in succession.</HeaderBody>
      <HeaderBody>Keep a track of your spending, maybe hire some mid carders?</HeaderBody>
    </Box>
  ),
  alertProductionGuided: (
    <Box>
      <HeaderTertiary>A simpler way to plan</HeaderTertiary><br />
      <HeaderBody>The guided production mode lets you oversee all segments, with the option to drag and drop order segments to!</HeaderBody>
      <HeaderBody>Click "update" to view the more detailed segment planner, or click Preview to see the poster view.</HeaderBody>
      <HeaderBody>Click on the complete button at the bottom of the segments to close the show</HeaderBody>
    </Box>
  ),
  alertDraftImport: (
    <Box>
      <HeaderTertiary>Bring in some new talent</HeaderTertiary><br />
      <HeaderBody>You can import new wrestlers into the draft pool below, some can be very expensive but have legendary careers to utilise</HeaderBody>
    </Box>
  ),
  alertDraftIntro: (
    <Box>
      <HeaderTertiary>Lets shake things up</HeaderTertiary><br />
      <HeaderBody>This is everyones opportunity to shape a brand new roster with a new budget!</HeaderBody>
    </Box>
  ),
  alertImported: (
    <Box>
      <HeaderTertiary></HeaderTertiary><br />
      <HeaderBody></HeaderBody>
    </Box>
  ),
  alertLeaguesIntro: (
    <Box>
      <HeaderBody>Leagues for championships - add contenders and mix up the leagues!</HeaderBody>
    </Box>
  ),
  alertOfficeCalendar: (
    <Box>
      <HeaderBody>Heres an overview of the shows that you need to book for this week, fortnight, month or year.</HeaderBody>
    </Box>
  ),
  alertOfficeIntro: (
    <Box>
      <HeaderTertiary>Welcome to your new office!</HeaderTertiary><br />
      <HeaderBody>We'll provide an overview of everything happening to keep a track of your federations</HeaderBody>
    </Box>
  ),
  alertProductionComplete: (
    <Box>
      <HeaderBody>If you are ready, start processing the production in stages by clicking "Next Step". Each time you click it we will update information across the board and simulate your actions.</HeaderBody>
    </Box>
  ),
  alertSaveLoadOverview: (
    <Box>
      <HeaderBody>You can download game data in JSON files; you can edit and upload these at any time.</HeaderBody><br />
      <HeaderBody>We use your browsers localStorage which can be wiped at any time. Please make frequent saves.</HeaderBody>
    </Box>
  ),
  alertSimulateIntro: (
    <Box>
      <HeaderTertiary>Throw the dice</HeaderTertiary><br />
      <HeaderBody>Randomised matches can provide some wild stat changes for all wrestlers.</HeaderBody>
      <HeaderBody>Lets watch the ladder of success change!</HeaderBody>
    </Box>
  ),
  alertTournamentBrackets: (
    <Box>
      <HeaderTertiary>Dream match ups</HeaderTertiary><br />
      <HeaderBody>Click wrestlers to add them in order to the tournament.</HeaderBody>
      <HeaderBody>You'll be able to select who wins at each stage, so plan with your result brackets in mind!</HeaderBody>
    </Box>
  ),
  alertTournamentEntries: (
    <Box>
      <HeaderTertiary>Size it up</HeaderTertiary><br />
      <HeaderBody>Select the amount of entrants you'd like to add for your tournament</HeaderBody>
    </Box>
  ),
}

const modelKeys = Object.keys(modelContent)

export function TutorialAlert({ modelKey = modelKeys[0], withSpace = false, severity = 'info' }) {
  const { model, onUpdate } = useGame()
  const visible = model?.[modelKey] || false
  const onToggle = () => onUpdate({ [modelKey]: !visible })

  return (
    <Box sx={{ width: '100%', marginBottom: visible && withSpace ? "2rem" : "0" }}>
      <Collapse in={visible} appear timeout={DURATIONTIMEOUT / 2}>
        <Alert
          icon={<FSXLogo variant={2} />}
          severity={severity as AlertColor}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={onToggle}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          {modelContent[modelKey]}
        </Alert>
      </Collapse>
    </Box>
  )
}

export const ManageAllTutorialAlerts = () => {
  const { model, onUpdate } = useGame()
  const onToggleValue = (key: string) => () => {
    const value = model?.[key] || false
    onUpdate({ [key]: !value })
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', flexBasis: '50%' }}>
      {modelKeys.map(modelKey => {
        const visible = model?.[modelKey] || false
        const color = visible ? 'success.main' : 'error.main'
        const Icon = visible ? Off : On
        return (
          <SpacedPaper
            key={modelKey}
            onClick={onToggleValue(modelKey)}
            sx={{
              color: 'inherit',
              backgroundColor: 'background.paper',
              alignItems: 'center',
              boxShadow: '3px 3px #b1adad',
              display: 'flex',
              height: "auto",
              minHeight: "2rem",
              flexDirection: 'row',
              width: "100%",
              mb: ".5rem",
              justifyContent: 'space-between',
            }}
          >
            <HeaderBody tabIndex={0}>
              {modelKey.replace('alert', '').split(/(?=[A-Z])/).join(' ')}
            </HeaderBody>
            <Icon sx={{ color }} />
          </SpacedPaper>
        )
      })}
    </Box>
  )
}

export default TutorialAlert
